import { isReservedWord } from './reservedWords';
import type { EntityType } from './types';
import { buildEntityNamesDictionary, type ValidateEntityTypes } from './validateEntityName';

/**
 * Transforms an entity name to ensure it meets our validation rules
 * If the name is already valid, returns it as is
 * If not, attempts to fix it by:
 * 1. Ensuring it starts with a letter
 * 2. Removing invalid characters
 * 3. Ensuring minimum length of 3 characters
 */
function sanitizeName({ name, entityType }: { name: string; entityType: EntityType }): string {
  if (!name) {
    return entityType;
  }

  let result = name;

  // Ensure name starts with a letter
  if (!/^[A-Za-z]/.test(result)) {
    result = `${entityType}${result}`;
  }

  // Replace invalid characters with underscores
  result = result.replace(/\W/g, '_');

  // Ensure minimum length of 3
  if (result.length < 3) {
    result = `${entityType}${result}`;
  }

  return result;
}

/**
 * Creates a new unique name for an entity
 * If the original name is valid and doesn't collide, returns it
 * Otherwise, fixes and/or increments it until it's valid and unique
 */
export function fixEntityName({
  originalName,
  entityType,
  pageVariables,
  pageFunctions,
  blocks,
  folders,
  dataSources,
  walkthroughSteps,
  entityId,
}: {
  originalName: string;
  entityType: EntityType;
  entityId?: string;
} & ValidateEntityTypes): string {
  // First sanitize the name to make it conform to our format rules
  const sanitizedName = sanitizeName({
    name: originalName,
    entityType,
  });

  const namesDict = buildEntityNamesDictionary({
    pageVariables,
    pageFunctions,
    blocks,
    folders,
    dataSources,
    walkthroughSteps,
  });

  const isNameTaken = (name: string): boolean => {
    const normalizedName = name.toLowerCase();
    const existingId = namesDict[entityType].get(normalizedName);

    // Not taken if no entity exists with this name or if it's the entity being renamed
    return existingId !== undefined && existingId !== entityId;
  };

  const hasTypeCollision = (name: string): boolean => {
    const normalizedName = name.toLowerCase();
    const typesToCheck = Object.keys(namesDict).filter(
      (type) => type !== entityType,
    ) as EntityType[];

    for (const type of typesToCheck) {
      if (namesDict[type].has(normalizedName)) {
        return true;
      }
    }

    return false;
  };

  const isUnavailable = (name: string): boolean =>
    isNameTaken(name) || hasTypeCollision(name) || isReservedWord(name);

  // If the sanitized name is available, use it
  if (!isUnavailable(sanitizedName)) {
    return sanitizedName;
  }

  // Otherwise, append numbers until we find an available name
  let counter = 1;
  let newName = `${sanitizedName}${counter.toString()}`;

  while (isUnavailable(newName)) {
    counter++;
    newName = `${sanitizedName}${counter.toString()}`;
  }

  return newName;
}

export const replaceInvalidCharacters = (name: string): string => {
  // Replace invalid characters with underscores
  return name.replace(/\W/g, '_');
};
